/**
 * <sticky-header> — the site navbar.
 *
 * Responsibilities, in the order they appear below:
 *   1. Scroll behaviour: 'smart' mode hides on scroll down / shows on scroll up,
 *      'sticky' mode (product pages) stays put. Both drop `.transparent` after
 *      100px so the bar picks up its glassy background.
 *   2. Colour scheme: an IntersectionObserver reads the `data-color-scheme` of
 *      whichever section is under the bar and mirrors it onto
 *      `data-current-skin`, so the bar's text flips light/dark.
 *   3. UpPromote affiliate-banner offset (see syncAffiliateOffset).
 *   4. Desktop dropdowns: Shop / Learn open on hover, Search opens on click.
 *   5. Mobile drawer with drill-in panels and a shared back button.
 *
 * Search itself lives in <header-search> (assets/header-search.js). The only
 * coupling is in this file's SEARCH section near the bottom: we listen for
 * `header-search:query` to swap the drawer's menu for results, and call
 * reset() on a surface when its panel closes.
 */

class StickyHeader extends HTMLElement {
  constructor() {
    super()
    this.lastScrollTop = 0
    this.ticking = false
    this.headerBounds = 0
    this.affiliateBannerHeight = 0
    this.header = this.querySelector(".site-header")

    // Desktop dropdowns
    this.navLinks = this.querySelectorAll(".site-header-nav .site-header-link")
    this.searchTrigger = this.querySelector(
      '.site-header-side-end [data-target="search"]'
    )
    this.desktopMenu = this.querySelector("#desktop-mega-menu")
    this.panels = this.querySelectorAll(".mega-panel")
    this.activePanel = null
    // Only a hover-opened panel should close when the pointer leaves the
    // header. A click-opened one (search) stays until Escape / another click.
    this.hoverOpened = false

    // Mobile drawer
    this.mobileTrigger = this.querySelector("#site-header-mobile-trigger")
    this.mobileDrawer = this.querySelector("#mobile-drawer")
    this.mobileTriggers = this.querySelectorAll("[data-trigger-mobile]")
    this.mobileBackBtns = this.querySelectorAll("[data-back-mobile]")

    // Search — the surfaces themselves are <header-search> elements.
    this.searchSurfaces = this.querySelectorAll("header-search")
    this.menuContent = this.querySelectorAll(
      "#mobile-panel-main [data-menu-content]"
    )

    this.onScrollBound = this.onScroll.bind(this)
    this.onKeydownBound = this.onKeydown.bind(this)
  }

  connectedCallback() {
    const sections = document.querySelectorAll(
      "[data-color-scheme]:not(.slider-slide)"
    )
    const productSlides = document.querySelectorAll(
      ".slider-slide[data-color-scheme]"
    )

    if (sections.length > 0) {
      // Use innerHeight to compute bottom margin in pixels.
      // Percentage-based rootMargin can produce a near-zero detection zone
      // on iOS Safari due to its dynamic viewport (toolbar area is included
      // in the layout viewport that IntersectionObserver measures against).
      const bottomMargin = -(window.innerHeight - 100)

      // Everything currently in the sensor strip. Reacting only to elements
      // that ENTER the strip misses a sticky element that never leaves it
      // (the V3 Max PDP gallery on mobile): once the info card scrolls over it
      // and back off, nothing new enters and the bar keeps the card's skin.
      // So keep the set and let the last one in DOM order win, which is the
      // one painted on top (a card over a sticky gallery, a child over its
      // section). An empty strip keeps the current skin.
      const underBar = new Set()

      const sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) underBar.add(entry.target)
            else underBar.delete(entry.target)
          })

          let top = null
          underBar.forEach((el) => {
            if (
              !top ||
              top.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
            ) {
              top = el
            }
          })
          if (!top) return

          // 'dark' section bg = 'dark' skin (light text), 'light' = ink text
          const scheme = top.getAttribute("data-color-scheme")
          this.setAttribute("data-current-skin", scheme === "dark" ? "dark" : "light")
        },
        {
          // ROOT MARGIN IS THE KEY
          // Top: -80px (Adjust to your header height, offsets the trigger point)
          // Bottom: computed px value keeps a ~100px detection strip under the
          //         header. Avoids the iOS Safari percentage rootMargin bug.
          rootMargin: `-80px 0px ${bottomMargin}px 0px`,
          threshold: 0,
        }
      )

      sections.forEach((s) => sectionObserver.observe(s))
    }
    productSlides.forEach((s) => this.observeProductSlide(s))
    // 2. Read the mode we set in Liquid
    this.mode = this.getAttribute("data-mode")

    // 3. Cache the height so we know when to start hiding (avoid hiding immediately at top)
    this.headerBounds = this.offsetHeight

    // 4. Bind the scroll event (passive for performance)
    window.addEventListener("scroll", this.onScrollBound, { passive: true })
    document.addEventListener("keydown", this.onKeydownBound)

    // 5. Offset the header below the UpPromote affiliate banner while it's in
    //    view. UpPromote injects a `.scaaf-message-bar` at the top of the
    //    document asynchronously after load and reserves space via an inline
    //    `padding-top` on <body> (height set in its dashboard). The header is
    //    position:fixed so it ignores that padding. We detect the bar by its
    //    reserved padding / element rather than the `is-affiliate-link` body
    //    attribute — UpPromote drops that attribute on pages past the landing
    //    page even though the bar stays. The bar scrolls away with the page, so
    //    we offset only near the top (per-scroll in updateAffiliateOffset) and
    //    release it once the bar is gone. The observer tracks when UpPromote
    //    injects, resizes, or removes the bar.
    //
    //    The sitewide announcement banner (.announcement-bar) is position:fixed
    //    so it must be pushed down by the UpPromote bar height and tracked
    //    per-scroll (since it doesn't auto-scroll away) — see syncAffiliateOffset
    //    and updateAffiliateOffset.
    this.topBanner = document.querySelector(".announcement-bar")
    this.syncAffiliateOffset()
    this.affiliateObserver = new MutationObserver(
      this.syncAffiliateOffset.bind(this)
    )
    this.affiliateObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["is-affiliate-link", "style"],
      childList: true,
    })

    // ---- Desktop dropdowns -------------------------------------------------
    this.navLinks.forEach((link) => {
      const targetId = link.getAttribute("data-target")

      link.addEventListener("mouseenter", () => {
        if (targetId) {
          this.openMegaMenu(targetId, { hover: true })
        } else {
          // Hovering a plain link (Special Offer, About) dismisses the dropdown
          this.closeMegaMenu()
        }
      })

      if (!targetId) return
      // Keyboard and touch users get the same panel on click.
      link.addEventListener("click", (event) => {
        event.preventDefault()
        if (this.activePanel === targetId) {
          this.closeMegaMenu()
        } else {
          this.openMegaMenu(targetId)
        }
      })
    })

    if (this.searchTrigger) {
      this.searchTrigger.addEventListener("click", () => {
        if (this.activePanel === "search") {
          this.closeMegaMenu()
          return
        }
        this.openMegaMenu("search")
        this.searchSurfaces.forEach((el) => {
          if (el.dataset.surface !== "desktop") return
          if (typeof el.focusInput === "function") el.focusInput()
        })
      })
    }

    this.addEventListener("mouseleave", () => {
      if (this.hoverOpened) this.closeMegaMenu()
    })
    this.addEventListener("focusout", (e) => {
      if (!this.contains(e.relatedTarget)) {
        this.closeMegaMenu()
      }
    })

    // ---- Mobile drawer -----------------------------------------------------
    if (this.mobileTrigger) {
      this.mobileTrigger.addEventListener("click", () => {
        const isOpen = this.mobileDrawer.classList.toggle("is-active")

        this.mobileTrigger.classList.toggle("is-active", isOpen)
        this.header.classList.toggle("white-mobile", isOpen)
        this.mobileTrigger.setAttribute("aria-expanded", isOpen)

        this.closeMobileSubMenus()
        if (!isOpen) this.resetSearch()

        document.body.style.overflow = isOpen ? "hidden" : ""
      })
    }

    this.mobileTriggers.forEach((btn) => {
      btn.addEventListener("click", () => {
        const targetId = btn.getAttribute("data-trigger-mobile")
        const targetPanel = this.querySelector(`#mobile-panel-${targetId}`)
        if (!targetPanel) return

        targetPanel.classList.add("is-active")
        targetPanel.setAttribute("aria-hidden", "false")

        // Focus management (Accessibility)
        const firstFocusable = targetPanel.querySelector("button, a")
        if (firstFocusable) firstFocusable.focus()
      })
    })

    // Close the top drill-in (Back button)
    this.mobileBackBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        this.closeMobileSubMenus()
      })
    })

    // ---- Search ------------------------------------------------------------
    // <header-search> owns the query, the requests and the rows. All this
    // controller does is give the drawer's menu its turn: while a query is
    // present the results take its place, and it comes back when the query is
    // cleared. See assets/header-search.js.
    this.addEventListener("header-search:query", (event) => {
      if (event.detail.surface !== "mobile") return
      this.menuContent.forEach((el) => {
        el.hidden = event.detail.hasQuery
      })
    })
  }

  disconnectedCallback() {
    // Clean up memory when section is removed
    window.removeEventListener("scroll", this.onScrollBound)
    document.removeEventListener("keydown", this.onKeydownBound)
    document.body.style.overflow = ""
    if (this.affiliateObserver) this.affiliateObserver.disconnect()
  }

  onKeydown(event) {
    if (event.key !== "Escape") return
    if (this.activePanel) {
      const wasSearch = this.activePanel === "search"
      this.closeMegaMenu()
      if (wasSearch && this.searchTrigger) this.searchTrigger.focus()
      return
    }
    if (
      this.mobileDrawer &&
      this.mobileDrawer.classList.contains("is-active")
    ) {
      if (this.querySelector(".mobile-panel.is-active")) {
        this.closeMobileSubMenus()
      } else if (this.mobileTrigger) {
        this.mobileTrigger.click()
      }
    }
  }

  // Watch a PDP gallery slide's `data-color-scheme` (mobile only). Public so
  // the product gallery can hand over the variant slide it adds or re-skins
  // on variant change: re-observing fires a fresh entry, so a slide already
  // under the header applies its new scheme right away.
  observeProductSlide(slide) {
    if (!this.productSlidesObserver) {
      this.productSlidesObserver = new IntersectionObserver(
        (entries) => {
          if (window.matchMedia("(min-width: 1024px)").matches) return
          entries.forEach((entry) => {
            // If a section enters the "Sensor Zone" at the top
            if (entry.isIntersecting) {
              const scheme = entry.target.getAttribute("data-color-scheme")

              // Apply logic to header
              // 'dark' section bg = 'light' text skin
              // 'light' section bg = 'dark' text skin
              const skin = scheme === "dark" ? "dark" : "light"

              this.setAttribute("data-current-skin", skin)
            }
          })
        },
        {
          // ROOT MARGIN IS THE KEY
          // Top: -80px (Adjust to your header height, offsets the trigger point)
          // We only want to know what is effectively "Under" the header.
          rootMargin: "-80px 0px 0px 0px",
          threshold: 0.5,
        }
      )
    }
    this.productSlidesObserver.unobserve(slide)
    this.productSlidesObserver.observe(slide)
  }

  unobserveProductSlide(slide) {
    if (this.productSlidesObserver) this.productSlidesObserver.unobserve(slide)
  }

  // Cache the UpPromote affiliate banner height, then re-apply the offset.
  // Detect the bar by the space it reserves, NOT the `is-affiliate-link`
  // attribute (UpPromote strips that after the landing page while the bar
  // stays). Prefer the `.scaaf-message-bar` element height — reading
  // body.style.paddingTop is unreliable on product pages where we override it
  // ourselves to the full total. Fall back to body.style.paddingTop only when
  // no bar element is found and we haven't set an override yet. Called by the
  // MutationObserver; the per-scroll portion is in updateAffiliateOffset.
  syncAffiliateOffset() {
    const barEl = document.querySelector(".scaaf-message-bar")
    let height
    if (barEl) {
      height = barEl.offsetHeight
    } else {
      // No bar element: if we own body.paddingTop on a product page it already
      // reflects the full total, not just the affiliate share — so don't read it.
      height = document.querySelector(".product-utf")
        ? 0
        : parseFloat(document.body.style.paddingTop) || 0
    }
    this.affiliateBannerHeight = height

    // On product pages (desktop), body.style.paddingTop must cover the entire
    // fixed stack: affiliate bar + announcement bar + sticky navbar. The CSS
    // rules in product.liquid handle affiliate-absent cases, but UpPromote's
    // inline body.style.paddingTop (set to their bar height) overrides them.
    // We take ownership here so both bars together produce the right offset.
    if (
      document.querySelector(".product-utf") &&
      window.matchMedia("(min-width: 1024px)").matches
    ) {
      const annHeight = this.topBanner ? this.topBanner.offsetHeight : 0
      const navHeight = this.offsetHeight
      const needed = height + annHeight + navHeight
      const current = parseFloat(document.body.style.paddingTop) || 0
      if (Math.abs(current - needed) > 1) {
        document.body.style.paddingTop = needed + "px"
      }
    }

    // .announcement-bar is position:fixed, so per-scroll tracking is handled
    // inside updateAffiliateOffset — no static set needed here.
    this.updateAffiliateOffset()
  }

  // Offset the fixed header below the affiliate banner ONLY while the banner is
  // still on screen. The banner sits at the top of the document and scrolls
  // away, so we shrink the offset by how far we've scrolled and drop it to the
  // normal `top` (CSS 0.5rem) once the banner is gone. Keeping a permanent
  // offset would leave a dead gap down-page and stop the scroll-hide transform
  // (calibrated for top:0.5rem) from fully tucking the header away.
  updateAffiliateOffset() {
    const visible = Math.max(0, this.affiliateBannerHeight - window.scrollY)
    const top = visible > 0 ? `calc(0.5rem + ${visible}px)` : ""
    // Only write when the value changes, so we don't invalidate layout every
    // scroll frame (or on unrelated body-style mutations like the mobile-menu
    // overflow toggle).
    if (this.style.top !== top) this.style.top = top

    // .announcement-bar is position:fixed so it doesn't scroll away on its own.
    // Push it down by the visible portion of the referral bar, then release it
    // once the referral bar has scrolled fully out of view.
    if (this.topBanner) {
      const annTop = visible > 0 ? `${visible}px` : ""
      if (this.topBanner.style.top !== annTop) this.topBanner.style.top = annTop
    }
  }

  closeMobileSubMenus() {
    const activePanels = this.querySelectorAll(".mobile-panel.is-active")
    activePanels.forEach((p) => {
      p.classList.remove("is-active")
      p.setAttribute("aria-hidden", "true")
    })
  }

  openMegaMenu(targetId, options) {
    const targetPanel = this.querySelector(`#mega-panel-${targetId}`)
    if (!targetPanel) return

    this.activePanel = targetId
    this.hoverOpened = Boolean(options && options.hover)
    this.header.classList.add("white")
    this.desktopMenu.classList.add("is-active")

    this.panels.forEach((panel) => {
      const isTarget = panel === targetPanel
      panel.classList.toggle("is-active", isTarget)
      panel.setAttribute("aria-hidden", String(!isTarget))
    })

    this.syncTriggerState()
  }

  closeMegaMenu() {
    // A closed search panel keeps no query, so reopening it never shows rows
    // fetched against an older catalog.
    if (this.activePanel === "search") this.resetSearch("desktop")

    this.activePanel = null
    this.hoverOpened = false
    this.desktopMenu.classList.remove("is-active")
    this.header.classList.remove("white")

    // Panels keep their .is-active until the wrapper has faded out, which reads
    // better than snapping the content away; clearing aria keeps AT in sync.
    this.panels.forEach((panel) => panel.setAttribute("aria-hidden", "true"))
    this.syncTriggerState()
  }

  syncTriggerState() {
    const setExpanded = (el) => {
      if (!el) return
      const target = el.getAttribute("data-target")
      // Plain links (Special Offer, About) own no panel and carry no state.
      if (!target) return
      el.setAttribute("aria-expanded", String(this.activePanel === target))
    }
    this.navLinks.forEach(setExpanded)
    setExpanded(this.searchTrigger)
  }

  // ---- Search ---------------------------------------------------------------

  // Closing a panel is a clean slate: the query goes, the curated idle rows
  // come back. Called with no argument when the whole drawer closes.
  resetSearch(surface) {
    this.searchSurfaces.forEach((el) => {
      if (surface && el.dataset.surface !== surface) return
      // The element may not have upgraded yet on a very early close.
      if (typeof el.reset === "function") el.reset()
    })
  }

  onScroll() {
    // 6. Throttle the scroll event to the screen refresh rate (60fps)
    // This prevents the browser from calculating pixels 1000 times a second.
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.handleScroll()
        this.ticking = false
      })
      this.ticking = true
    }
  }

  handleScroll() {
    const scrollTop = window.scrollY

    // Keep the header glued just under the affiliate banner as it scrolls off,
    // then release it once the banner is gone. Runs before the smart-mode
    // early-return below so it also applies on product pages (sticky mode).
    this.updateAffiliateOffset()

    if (scrollTop < 100) {
      this.header.classList.add("transparent")
    } else {
      this.header.classList.remove("transparent")
    }

    // 7. Guard Clause: Only run this logic if we are in 'smart' mode
    if (this.mode !== "smart") return

    // 8. Prevent negative scrolling (iOS elastic bounce) causing glitches
    if (scrollTop < 0) return

    // 9. Logic: Hide on scroll down, Show on scroll up
    // We also check (scrollTop > this.headerBounds) so it doesn't hide
    // while we are still hovering around the top area.
    if (scrollTop > this.lastScrollTop && scrollTop > this.headerBounds) {
      // SCROLLING DOWN
      this.classList.add("is-hidden")
    } else {
      // SCROLLING UP
      this.classList.remove("is-hidden")
    }

    // 10. Save current position for next comparison
    this.lastScrollTop = scrollTop
  }
}

// Register the component so HTML recognizes <sticky-header>
customElements.define("sticky-header", StickyHeader)
